<div class="price-details-tooltip" (click)="$event.stopPropagation()">
  <div class="tooltip-header">
    {{ 'quotations_and_orders.price_details_title' | translate }}
  </div>

  <div class="tooltip-body">
    <!-- System price row -->
    <div class="tooltip-row">
      <span class="row-label">{{ 'quotations_and_orders.system_price' | translate }}</span>
      <span class="row-value">{{ formatPrice(product.catalog_price) }}</span>
      <button
        *ngIf="!isReadOnly && !isSystemApplied && product.catalog_price != null"
        class="apply-btn"
        (click)="onApplySystemPrice($event)"
      >
        <ui-svg-icon svgIcon="rollback" size="small"></ui-svg-icon>
        {{ 'common.apply' | translate | uppercase }}
      </button>
    </div>

    <!-- AI parsed price row -->
    <div class="tooltip-row">
      <span class="row-label">
        {{ 'quotations_and_orders.parsed_data' | translate }}
        <ui-svg-icon svgIcon="ai-volta-icon" size="small"></ui-svg-icon>
      </span>
      <span class="row-value">{{ formatPrice(displayedParsedPrice) }}</span>
      <button
        *ngIf="!isReadOnly && !isAiApplied && product.ai_price != null"
        class="apply-btn"
        (click)="onApplyAiPrice($event)"
      >
        <ui-svg-icon svgIcon="rollback" size="small"></ui-svg-icon>
        {{ 'common.apply' | translate | uppercase }}
      </button>
    </div>
    <div class="tooltip-note" *ngIf="showConversionNote">
      {{ 'quotations_and_orders.side_by_side_panel.sub_unit_conversion_note' | translate }}
    </div>

    <!-- No price row -->
    <div class="tooltip-row">
      <span class="row-label">{{ 'quotations_and_orders.no_price_option' | translate }}</span>
      <span class="row-value">{{ formatPrice(null) }}</span>
      <button
        *ngIf="!isReadOnly && !isNoneApplied"
        class="apply-btn"
        (click)="onApplyNoPrice($event)"
      >
        <ui-svg-icon svgIcon="rollback" size="small"></ui-svg-icon>
        {{ 'common.apply' | translate | uppercase }}
      </button>
    </div>
    <!-- Other known prices, below the three fixed rows above. Each candidate
         is a `tooltip-row`, exactly like the three above it: same padding,
         same label column, same value column, same APPLY. The whole section
         is absent when the engine returned nothing. -->
    <div class="tooltip-candidates" *ngIf="showCandidatesSection">
      <div class="candidates-title">
        {{ 'quotations_and_orders.other_known_prices' | translate }}
      </div>

      <ng-container *ngFor="let row of visibleRows; trackBy: trackByRow">
        <div class="tooltip-row" [class.candidate-expired]="row.expired">
          <span class="row-label">
            <span class="candidate-source">{{ row.sourceKey | translate }}</span>
            <span class="candidate-freshness" [ngClass]="'freshness-' + row.freshness">
              {{ row.freshnessKey | translate }}
            </span>
          </span>
          <span class="row-value candidate-value">{{ row.priceLabel }}</span>
          <button
            *ngIf="!isReadOnly && !row.expired"
            class="apply-btn"
            (click)="onApplyCandidate($event, row)"
          >
            <ui-svg-icon svgIcon="rollback" size="small"></ui-svg-icon>
            {{ 'common.apply' | translate | uppercase }}
          </button>
        </div>
        <!-- Everything that qualifies the price rather than being it, on the
             note line the tooltip already uses for the conversion caveat. -->
        <div class="tooltip-note candidate-meta" *ngIf="row.evidenceKey || row.audienceKey || row.candidate.observed_at || row.provenance || row.occurrences">
          <span class="candidate-evidence" *ngIf="row.evidenceKey">{{ row.evidenceKey | translate }}</span>
          <!-- A tariff published for everyone sits next to this customer's own:
               without this the two rows read as two prices from one source. -->
          <span class="candidate-audience" *ngIf="row.audienceKey">{{ row.audienceKey | translate }}</span>
          <span class="candidate-occurrences" *ngIf="row.occurrences">{{ row.occurrences }}</span>
          <span class="candidate-date" *ngIf="row.candidate.observed_at">{{ row.candidate.observed_at | date: DATE_FORMAT }}</span>
          <!-- The provenance NAMES the file; when the file was kept, the eye
               beside it opens it — the app's own preview affordance, and the
               same one the full panel offers. -->
          <span class="candidate-provenance" *ngIf="row.provenance">{{ row.provenance }}</span>
          <button
            *ngIf="row.provenance && row.documentId"
            type="button"
            class="preview-icon"
            [matTooltip]="'common.preview' | translate"
            matTooltipPosition="above"
            [attr.aria-label]="'common.preview' | translate"
            (click)="onPreviewSource($event, row)"
          >
            <ui-svg-icon svgIcon="eye" size="small"></ui-svg-icon>
          </button>
        </div>
      </ng-container>

      <a class="candidates-see-all" *ngIf="showSeeAllLink" (click)="onSeeAll($event)">
        {{ 'quotations_and_orders.see_other_known_prices' | translate:{ count: hiddenCandidateCount } }}
      </a>
    </div>
  </div>

  <!-- The APPLY buttons above are gone because the server refuses pricing on
       this order, not because nothing is applicable. Saying so is the
       difference between a locked screen and a broken one. -->
  <div class="tooltip-note tooltip-locked" *ngIf="noPriceLocked">
    {{ 'quotations_and_orders.no_price_order_locked_hint' | translate }}
  </div>

  <!-- Footer: current unit price + badge -->
  <div class="tooltip-footer">
    <span class="footer-label">{{ 'quotations_and_orders.order_line_columns.unit_price' | translate }}</span>
    <span class="footer-value">{{ isNoneApplied ? formatPrice(null) : formatPrice(product.final_price) }}</span>
    <!-- One key per source, derived, never a list of *ngIf: a per-source
         *ngIf list renders an empty badge for any source it does not
         enumerate. -->
    <span class="price-badge" [ngClass]="'badge-' + currentPriceType">
      <ui-svg-icon *ngIf="isAiApplied" svgIcon="ai-volta-icon" size="xsmall"></ui-svg-icon>
      {{ currentPriceSourceKey | translate }}
    </span>
  </div>

  <!-- Why this price and not another, rebuilt from the structured half of
       the trace. `decided_by` is a server diagnostic (it names an Odoo model
       and a field, in English whatever the language) and stays in the
       logs. -->
  <div class="tooltip-why" *ngIf="why">
    <span class="why-label">{{ 'quotations_and_orders.why_this_price' | translate }}</span>
    <span class="why-value">{{ why!.key | translate: why!.params }}</span>
  </div>

  <!-- The guard rail of the direct capture: when the engine's own selection
       differs from the number on the line, say so and offer to apply it —
       this line only, never the whole order. -->
  <div class="tooltip-why tooltip-settings-preferred" *ngIf="settingsPreferred">
    <span class="why-label">{{ 'quotations_and_orders.per_your_settings' | translate }}</span>
    <span class="why-value">
      {{ settingsPreferred!.sourceKey | translate }} {{ settingsPreferred!.priceLabel }}
    </span>
    <button
      *ngIf="!isReadOnly"
      class="apply-btn"
      (click)="onApplySettingsPreferred($event)"
    >
      <ui-svg-icon svgIcon="rollback" size="small"></ui-svg-icon>
      {{ 'common.apply' | translate | uppercase }}
    </button>
  </div>
</div>
